import React, { useEffect, useMemo, useRef, useState } from "react";
import { useExpanded, usePagination, useResizeColumns, useSortBy, useTable } from "react-table";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import { dateStrToYmd, getFirstDay, getReportName } from '@/shared/helpers';
import getUserStatusList from "@/services/getUserStatuses";
import { TABLE_COLUMNS, PEER2PEER_COLUMNS } from "./columns";
import { connect } from "react-redux";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  Sorting,
} from "@/shared/apiTableHelper";
import { clone } from 'lodash';
import ParticipantAccountSummaryFilter from "./ParticipantAccountSummaryFilter";

const queryClient = new QueryClient();

const mergeClassNames = (...values) => values.filter(Boolean).join(' ');

const createBaseFilter = (programs, programId) => ({
  programs,
  createdOnly: false,
  reportKey: 'sku_value',
  programId,
  from: dateStrToYmd(getFirstDay()),
  to: dateStrToYmd(new Date()),
  statusId: '',
});

const DataTable = ({ organization, program, programs }) => {
  const [filter, setFilter] = useState(() => createBaseFilter(programs, program.id));
  const [useFilter, setUseFilter] = useState(false);
  const [trigger] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportToCsv, setExportToCsv] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const exportLink = useRef(null);

  useEffect(() => {
    if (!organization?.id || !program?.id) {
      return;
    }

    getUserStatusList(organization.id, program.id)
      .then((list) => {
        setStatusOptions(
          list.map((status) => ({ value: status.id, label: status.status }))
        );
      });
  }, [organization?.id, program?.id]);

  useEffect(() => {
    if (!program?.id || !Array.isArray(programs) || programs.length === 0) {
      return;
    }

    setFilter((previous) => ({
      ...previous,
      programs,
      programId: program.id,
    }));
    setUseFilter(true);
  }, [program?.id, programs]);

  const [{ queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/report/participant-account-summary`;
  const { isLoading, error, data, isSuccess } = useQuery(
    ['', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
    () => fetchApiData({
      url: apiUrl,
      page: queryPageIndex,
      size: queryPageSize,
      filter,
      sortby: queryPageSortBy,
      trigger: queryTrigger,
    }),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (exportToCsv && exportLink.current) {
      setExportToCsv(false);
      exportLink.current.link.click();
    }
  }, [exportLink, exportToCsv]);

  const download = async (filterValues) => {
    try {
      setIsExporting(true);

      const response = await fetchApiDataExport({
        url: apiUrl,
        filter: {
          ...clone(filterValues),
          exportToCsv: 1,
        },
        sortby: queryPageSortBy,
        trigger: queryTrigger,
      });

      const visibleColumns = allColumns.filter((column) => column.isVisible);
      const visibleKeys = visibleColumns.map((column) => column.id || column.accessor);

      const filteredHeaders = response.headers.filter((header) =>
        visibleKeys.includes(header.key)
      );

      const filteredResults = response.results.map((row) => {
        const nextRow = {};
        visibleKeys.forEach((key) => {
          nextRow[key] = row[key];
        });
        return nextRow;
      });

      setExportData(filteredResults);
      setExportHeaders(filteredHeaders);
      setExportToCsv(true);
    } catch (downloadError) {
      console.error("Export failed:", downloadError);
    } finally {
      setIsExporting(false);
    }
  };

  const baseColumns = useMemo(
    () => (
      program?.uses_peer2peer
        ? [...TABLE_COLUMNS, ...PEER2PEER_COLUMNS]
        : TABLE_COLUMNS
    ),
    [program?.uses_peer2peer]
  );

  const columns = useMemo(
    () => [
      ...baseColumns,
      {
        Header: 'Status',
        accessor: 'status',
        className: 'participant-account-summary__cell--status',
        Cell: ({ value }) => value || 'N/A',
      },
    ],
    [baseColumns]
  );

  const tableData = useMemo(
    () => (data ? Object.values(data.results || {}) : []),
    [data]
  );

  const currentTotalCount = data?.count || 0;
  const totalPageCount = Math.ceil(currentTotalCount / queryPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    allColumns,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
        hiddenColumns: [],
      },
      manualPagination: true,
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true,
      autoResetHiddenColumns: false,
      striped: true,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns
  );

  const manualPageSize = [];
  useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });

  const hasRows = page.length > 0;
  const visibleColumnCount = allColumns.filter((column) => column.isVisible).length;

  if (isLoading) {
    return (
      <section className="manager-report-panel manager-report-panel--state">
        Loading participant balances...
      </section>
    );
  }

  if (error) {
    return (
      <section className="manager-report-panel manager-report-panel--state">
        Unable to load participant account summary.
      </section>
    );
  }

  if (!isSuccess) {
    return null;
  }

  return (
    <section
      className={mergeClassNames(
        "manager-report-panel manager-report-panel--table participant-account-summary-report",
        program?.uses_peer2peer && "participant-account-summary-report--peer"
      )}
    >
      <div className="manager-report-panel__filters">
        <ParticipantAccountSummaryFilter
          availablePrograms={programs}
          filter={filter}
          setFilter={setFilter}
          setUseFilter={setUseFilter}
          exportData={exportData}
          exportLink={exportLink}
          exportHeaders={exportHeaders}
          download={download}
          isExporting={isExporting}
          reportName={getReportName('ParticipantAccountSummary')}
          statusOptions={statusOptions}
        />
      </div>

      <div className="manager-report-table-shell">
        <div className="manager-report-table-shell__actions">
          <span className="manager-report-table-shell__results">
            Showing {page.length.toLocaleString()} of {currentTotalCount.toLocaleString()} matched participants
          </span>

          <UncontrolledDropdown>
            <DropdownToggle caret className="btn btn-outline-secondary manager-report-table-shell__toggle">
              Columns ({visibleColumnCount})
            </DropdownToggle>
            <DropdownMenu>
              {allColumns.map((column) => (
                <DropdownItem key={column.id} toggle={false}>
                  <input
                    type="checkbox"
                    {...column.getToggleHiddenProps()}
                    style={{ marginRight: "8px" }}
                  />
                  <label>{column.Header}</label>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <div className="manager-report-table-shell__wrap">
          {hasRows ? (
            <table {...getTableProps()} className="table table-striped manager-report-table">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const headerProps = column.getHeaderProps(column.getSortByToggleProps());

                      return (
                        <th
                          {...headerProps}
                          className={mergeClassNames(headerProps.className, column.className)}
                        >
                          {column.render('Header')}
                          {column.isSorted ? <Sorting column={column} /> : ''}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className="table table--bordered" {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  const subCount = (row.id.match(/\./g) || []).length;
                  const subRows = row.subRows;
                  const countSubRows = subRows ? subRows.length : 0;
                  const rowSpan = countSubRows ? countSubRows + 1 : 1;

                  return (
                    <React.Fragment key={`row-group-${row.id}`}>
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          if (cell.value === 'skip_td') {
                            return null;
                          }

                          const paddingLeft = subCount * 20;
                          const cellProps = cell.getCellProps();

                          return (
                            <td
                              {...cellProps}
                              rowSpan={rowSpan}
                              key={cell.column.id + row.id}
                              className={mergeClassNames(cellProps.className, cell.column.className)}
                              data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}
                            >
                              <span style={cell.column.Header === '#' ? { paddingLeft: `${paddingLeft}px` } : null}>
                                {cell.render('Cell')}
                              </span>
                            </td>
                          );
                        })}
                      </tr>

                      {countSubRows > 0 && subRows.map((subRow) => {
                        prepareRow(subRow);

                        return (
                          <tr {...subRow.getRowProps()} key={subRow.id}>
                            {subRow.cells.map((subCell) => {
                              if (subCell.value === 'skip_td') {
                                return null;
                              }

                              const subCellProps = subCell.getCellProps();

                              return (
                                <td
                                  {...subCellProps}
                                  key={subCell.column.id + subRow.id}
                                  className={mergeClassNames(subCellProps.className, subCell.column.className)}
                                  data-label={typeof subCell.column.Header === 'string' ? subCell.column.Header : ''}
                                >
                                  <span>{subCell.render('Cell')}</span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="manager-report-table__empty">
              No participant accounts match the current filters.
            </div>
          )}
        </div>
      </div>

      {rows.length > 0 && (
        <ReactTablePagination
          page={page}
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageOptions={pageOptions}
          pageSize={pageSize}
          pageIndex={pageIndex}
          pageCount={pageCount}
          setPageSize={setPageSize}
          manualPageSize={manualPageSize}
          dataLength={currentTotalCount}
        />
      )}
    </section>
  );
};

const TableWrapper = ({ organization, program, programs }) => {
  if (!organization || !program) {
    return (
      <section className="manager-report-panel manager-report-panel--state">
        Loading participant account summary...
      </section>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <DataTable
        organization={organization}
        program={program}
        programs={programs}
      />
    </QueryClientProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    program: state.program,
    organization: state.organization,
  };
};

export default connect(mapStateToProps)(TableWrapper);
